export default {
    props: {
        /**
         * Label to render above the input field when active
         */
        label: {
            type: String,
            default: '',
        },

        /**
         * Placeholder text for when the input is empty
         */
        placeholder: {
            type: String,
            default: '',
        },

        /**
         * HTML input element name
         */
        name: {
            type: String,
            default: '',
        },

        /**
         * Control hiding the input
         */
        hidden: Boolean,

        /**
         * Prevent user from typing in the input
         */
        readonly: Boolean,

        /**
         * Required field for validation purposes
         */
        required: Boolean,

        /**
         * Number input usage to control how much to increase/decrease with buttons. Also controls how the value is validated.  If set to "any", allows decimal numbers.
         */
        step: [String, Number],

        /**
         * Controls visualization of rendering form validation error states
         */
        submitted: Boolean,

        /**
         * Forces invalid form field
         */
        invalid: Boolean,

        /**
         * Form validation pattern regex
         */
        pattern: {
            type: String,
            default: '',
        },

        /**
         * Input HTML type
         */
        type: {
            type: String,
            default: 'text',
            options: ['text', 'email', 'password', 'number', 'submit', 'search', 'url', 'tel'],
        },

        /**
         * Control trimming the text value from the input on change to vmodel
         */
        trim: Boolean,

        /**
         * Min length allowed on the input for validation purposes
         */
        minlength: {
            type: Number,
            default: 0,
        },

        /**
         * Max length allowed on the input, will prevent user from being able to enter more
         */
        maxlength: {
            type: Number,
            default: 232,
        },

        /**
         * Min number allowed for number input
         */
        min: Number,

        /**
         * Max number allowed for number input
         */
        max: Number,

        /**
         * Input value
         */
        value: [String, Number],

        /**
         * Auto focus on this input containing component renders
         */
        autofocus: Boolean,

        /**
         * Control whether you allow users to have the ability to autofill/autocomplete this input from things like Google
         */
        autocomplete: {
            type: [Boolean, String],
            default: false,
        },

        /**
         * Intercept input_update event
         */
        interceptOnUpdate: Function,

        /**
         * String list of classes to apply to the input element
         */
        inputClassList: {
            type: String,
            default: '',
        },
    },

    watch: {
        value(value) {
            this.input_setValue(value);
        },

        invalid() {
            this.input_checkInvalid();
        },
    },

    data() {
        return {
            input_dirty: false,
            input_localValue: this.value,
            input_active: false,
        };
    },

    computed: {
        input_computedPattern() {
            let pattern = this.pattern || '.*';

            if (this.minlength > 0) {
                pattern = `.{${this.minlength},}`;
            }

            return pattern;
        },

        input_passwordIcon() {
            return this.input_active ? 'eye-hide' : 'eye-show';
        },

        input_localType() {
            if (this.input_active) {
                return this.type === 'password' ? 'text' : 'password';
            }

            return this.type;
        },
    },

    created() {
        this.input_setValue(this.value);
    },

    mounted() {
        this.input_checkInvalid();
    },

    methods: {
        input_blur() {
            this.$refs.input.blur();
        },

        input_checkInvalid() {
            if (this.$refs.input) {
                if (this.invalid) {
                    this.$refs.input.setCustomValidity('invalid');
                } else {
                    this.$refs.input.setCustomValidity('');
                }
            }
        },

        input_focus() {
            this.$refs.input.focus();
        },

        input_handleBlur(event) {
            this.$emit('blur', event);

            const value = this.input_localValue;

            if (this.type === 'number' && (value === 0 || value === '')) {
                this.input_localValue = undefined;
                this.$nextTick(() => {
                    this.input_localValue = value;
                });
            }
        },

        input_handleSelect({ target } = { target: { selectionStart: null, selectionEnd: null } }) {
            const { selectionStart, selectionEnd } = target;

            this.$emit('selected', { selectionStart, selectionEnd });
        },

        input_highlight() {
            this.input_focus();
            this.$refs.input.setSelectionRange(0, this.value.length || 0);
        },

        input_isValid() {
            return this.$refs.input.validity.valid;
        },

        input_prevent(e) {
            if (this.type === 'number') {
                e.preventDefault();
            }
        },

        input_setValue(value) {
            let outOfRange;

            if (this.type === 'number') {
                const underFlow = value < this.min;
                const overFlow = value > this.max;

                outOfRange = underFlow || overFlow;
            }

            if (this.type === 'number' && outOfRange) {
                this.input_localValue = '';
                this.input_dirty = true;
            } else {
                this.input_localValue = (this.trim && value) ? value.trim() : value;
                this.input_dirty = value === 0 || Boolean(value);
            }
        },

        input_updateInput(value) {
            const updateInputCallback = (val) => {
                this.input_setValue(val);
                this.$emit('input', this.input_localValue);
            };

            if (this.interceptOnUpdate && typeof this.interceptOnUpdate === 'function') {
                this.interceptOnUpdate(value, updateInputCallback);
            } else {
                updateInputCallback(value);
            }
        },

        input_updateValueExternal(value) {
            this.$refs.input.value = value;
        },

        input_togglePassword() {
            this.input_active = !this.input_active;

            this.$nextTick(() => {
                this.input_focus();
            });
        },

        input_reportValidity() {
            if (!this.$refs.input || typeof this.$refs.input.reportValidity !== 'function') {
                return;
            }

            this.$refs.input.reportValidity();
        },
    },
};
